// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — applying light or dark to the page.
//
// The theme is `data-theme` on <html>; `mobile.scss` redefines its colour tokens
// under `:root[data-theme="dark"]`. Everything that paints — the stylesheet, the
// inline SVG, the state circles — reads those tokens, so this attribute is the
// whole switch.
//
// ⚠ IT RUNS FROM THE BUNDLE, and the bundle loads at the end of <body>: the
// page's CSP forbids an inline script in <head>, which is the only thing that
// could apply a stored choice before the first paint. `MobilePage.ts` calls
// `applyStoredAppearance()` as its very first statement, so the gap is the
// parsing of an empty <div> — but a dark-mode device may see one white frame.
// ─────────────────────────────────────────────────────────────────────────────

import { type Appearance, parseAppearance, themeFor, type Theme } from "home/app/mobile/appearance";

const APPEARANCE_KEY = "pqf.mobile.appearance";

/** The browser's own chrome — status bar, address bar — per theme. Mirrors `--color-bg`. */
const THEME_COLORS: Readonly<Record<Theme, string>> = {
    light: "#FFFFFF",
    dark: "#0E0E2C",
};

const DARK_QUERY = "(prefers-color-scheme: dark)";

/** The one listener for the phone's own setting, while `system` is chosen. */
let deviceListener: { query: MediaQueryList; handler: () => void } | null = null;

export function readStoredAppearance(): Appearance {
    try {
        return parseAppearance(window.localStorage.getItem(APPEARANCE_KEY));
    } catch {
        // Private mode, or site data blocked: the default, and nothing breaks.
        return parseAppearance(null);
    }
}

export function writeStoredAppearance(appearance: Appearance): void {
    try {
        window.localStorage.setItem(APPEARANCE_KEY, appearance);
    } catch {
        /* private mode: the choice simply does not survive a reload */
    }
}

function deviceIsDark(): boolean {
    return typeof window.matchMedia === "function" && window.matchMedia(DARK_QUERY).matches;
}

/**
 * Draw the page in `appearance`, and keep following the phone while it is `system`.
 *
 * Idempotent: the previous `system` listener is dropped first, so choosing twice never leaves two
 * listeners fighting over the attribute.
 */
export function applyAppearance(appearance: Appearance): Theme {
    if (deviceListener) {
        deviceListener.query.removeEventListener("change", deviceListener.handler);
        deviceListener = null;
    }

    const theme = themeFor(appearance, deviceIsDark());
    paintTheme(theme);

    if (appearance === "system" && typeof window.matchMedia === "function") {
        const query = window.matchMedia(DARK_QUERY);
        const handler = () => paintTheme(themeFor("system", query.matches));
        query.addEventListener("change", handler);
        deviceListener = { query, handler };
    }

    return theme;
}

/** Apply whatever this device chose last. The bundle's first statement. */
export function applyStoredAppearance(): Theme {
    return applyAppearance(readStoredAppearance());
}

function paintTheme(theme: Theme): void {
    document.documentElement.dataset.theme = theme;
    // The status bar of an installed web app takes this colour; left white, a dark app would sit
    // under a white strip.
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLORS[theme]);
}
